import { FC, useCallback } from "react";
import { addDays } from "date-fns";
import { Controller, useForm } from "react-hook-form";
import { Button, DatePicker, TextField, Typography } from "theme";
import { CrossLarge } from "theme/icons";

import { Box, Dialog, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { styled } from "@mui/system";

import { useCreatePromocode } from "../../../api/hooks/useCreatePromocode/useCreatePromocode.ts";
import DatepickerPopper from "../../../theme/components/DatePicker/DatepickerPopper.tsx";

interface PromocodeFormValues {
  promoCodeId: string;
  validTo: Date;
  usageCount: number;
}

interface CreatePromocodeModalProps {
  open: boolean;
  onClose: () => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    borderWidth: 1,
    borderStyle: "dashed",
    borderColor: theme.palette.secondary.main,
    backgroundColor: theme.palette.primary.main,
    minWidth: "420px",
    padding: theme.spacing(2),
  },
}));

const GenerateButton = styled("button")(({ theme }) => ({
  padding: theme.spacing(1, 2),
  background: "none",
  border: "1px solid",
  borderColor: theme.palette.secondary.main,
  cursor: "pointer",
  transition: "0.3s",
  whiteSpace: "nowrap",
  color: theme.palette.text.primary,

  "&:hover": {
    backgroundColor: theme.palette.secondary.contrastText,
  },
}));

const generateRandomCode = (length = 10): string => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
};

const CreatePromocodeModal: FC<CreatePromocodeModalProps> = ({ open, onClose }) => {
  const { control, handleSubmit, setValue, formState: { isSubmitting } } = useForm<PromocodeFormValues>({
    defaultValues: {
      promoCodeId: "",
      validTo: addDays(new Date(), 93),
      usageCount: 1,
    },
  });

  const { mutateAsync } = useCreatePromocode();

  const onGenerateCode = useCallback(() => {
    setValue("promoCodeId", generateRandomCode());
  }, [setValue]);

  const onSubmit = useCallback(
    async (data: PromocodeFormValues) => {
      await mutateAsync({
        promoCodeId: data.promoCodeId,
        promoCodeAmount: data.usageCount,
        usageCount: 0,
        validTo: data.validTo.toISOString(),
      });
      onClose();
    },
    [mutateAsync, onClose],
  );

  return (
    <StyledDialog open={open} onClose={onClose}>
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 0, mb: 2 }}>
        <Typography variant="h5" weight="semiBold">
          Створити промокод
        </Typography>
        <IconButton onClick={onClose} size="small">
          <CrossLarge />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box mb={3}>
            <Typography variant="body2" weight="semiBold" sx={{ mb: 1 }}>
              Промокод
            </Typography>
            <Box display="flex" alignItems="stretch" gap={1}>
              <Box flex={1}>
                <Controller
                  name="promoCodeId"
                  control={control}
                  rules={{ required: "Введіть промокод" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      placeholder="Введіть або згенеруйте код"
                      error={Boolean(error)}
                      helperText={error?.message}
                    />
                  )}
                />
              </Box>
              <GenerateButton type="button" onClick={onGenerateCode}>
                Згенерувати
              </GenerateButton>
            </Box>
          </Box>

          <Box mb={3}>
            <Typography variant="body2" weight="semiBold" sx={{ mb: 1 }}>
              Сума промокоду
            </Typography>
            <Controller
              name="usageCount"
              control={control}
              rules={{ required: "Введіть кількість", min: { value: 1, message: "Має бути більше 0" } }}
              render={({ field, fieldState: { error } }) => (
                <TextField
                  {...field}
                  type="number"
                  placeholder="Сума промокоду"
                  error={Boolean(error)}
                  helperText={error?.message}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                />
              )}
            />
          </Box>

          <Box mb={3}>
            <Typography variant="body2" weight="semiBold" sx={{ mb: 1 }}>
              Дійсний до
            </Typography>
            <Controller
              name="validTo"
              control={control}
              rules={{ required: "Оберіть дату" }}
              render={({ field }) => (
                <DatePicker
                  value={field.value}
                  onChange={(newValue) => field.onChange(newValue)}
                  slots={{
                    popper: DatepickerPopper,
                  }}
                  disablePast
                  showDaysOutsideCurrentMonth
                />
              )}
            />
          </Box>

          <Box display="flex" justifyContent="flex-end" gap={1}>
            <Button variant="outlined" look="light" onClick={onClose} type="button">
              Скасувати
            </Button>
            <Button variant="contained" look="normal" type="submit" disabled={isSubmitting}>
              Створити
            </Button>
          </Box>
        </form>
      </DialogContent>
    </StyledDialog>
  );
};

export default CreatePromocodeModal;
